import BreadCrumb from "Components/Common/BreadCrumb";
import React, { useEffect } from "react";
import { Container, Row, Col } from "reactstrap";
import Heading from "Components/Common/Heading";
import Filter from "Components/Common/Filter";
import Table, { Column } from "Components/Common/Table";
import { useMutation } from "@tanstack/react-query";
import { GetGuestAccessHistory } from "endpoints/guestAccess/index.action";
import Button from "Components/Common/Button";
import Pills from "Components/Common/Pills";
import { useTranslation } from "react-i18next";

// builder page
const AccessHistory = () => {
  const { t } = useTranslation();
  const [accessData, setAccessData] = React.useState<any[]>([]);
  const [page, setPage] = React.useState<number>(1);
  const [limit, setLimit] = React.useState<number>(10);
  const [search, setSearch] = React.useState<string>("");
  const [lastSearch, setLastSearch] = React.useState<string>("");

  const [paginationData, setPaginationData] = React.useState<{
    total: number;
    totalPages: number;
  }>({ total: 0, totalPages: 0 });
  const getRequestsMutation = useMutation({
    mutationKey: ["guestRequests"],
    mutationFn: async ({
      page,
      limit,
      search,
    }: {
      page: number;
      limit: number;
      search?: string;
    }) => {
      const res = await GetGuestAccessHistory({ page, limit, search });
      if (res?.statusCode === 200 && res.data) {
        const formattedData = res.data.guestPasses.map((req: any) => ({
          id: req.id,
          guest: req.guestName || "N/A",
          start: req?.startDate
            ? new Date(req.startDate).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "long",
                year: "numeric",
              }) +
              " -" +
              req.startTime
            : "N/A",
          end: req?.endDate
            ? new Date(req.endDate).toLocaleDateString("en-GB", {
                day: "numeric",
                month: "long",
                year: "numeric",
              }) +
              "-" +
              req.endTime
            : "N/A",
          otp: req.code,
          requestedBy: [req.resident?.firstName, req.resident?.lastName]
            .filter(Boolean)
            .join(" "),
          condo: req.resident?.residentProfile?.unit,
          req: new Date(req.createdAt).toLocaleString(),
          status: req.status
            ? req.status.charAt(0).toUpperCase() + req.status.slice(1)
            : "N/A",
          purposeOfVisit: req.purposeOfVisit || "N/A",
          requesteeProfileUrl: req.resident?.profileImage || null,
        }));
        setAccessData(formattedData);

        const computedTotal =
          typeof res.data.pagination.total === "number" &&
          res.data.pagination.total > 0
            ? res.data.pagination.total
            : (res.data.pagination.totalPages || 0) *
              (res.data.pagination.limit || limit);

        setPaginationData({
          total: computedTotal,
          totalPages: res.data.pagination.totalPages,
        });
      }
      return res;
    },
  });

  useEffect(() => {
    getRequestsMutation.mutate({ page, limit, search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, search]);

  const handlePageChange = (pageIndex: number, pageSize: number) => {
    // page number clicked
    setPage(pageIndex + 1);

    // ONLY update limit if it actually changed
    if (pageSize !== limit) {
      setLimit(pageSize);
      setPage(1); // reset page ONLY when page size changes
    }
  };

  const handleSearch = (v: string) => {
    if (v !== lastSearch) {
      setSearch(v);
      setLastSearch(v);
      setPage(1); // Only reset when search changes
    }
  };
  document.title = t("pastGuestAccess.title");

  const columns: Column<any>[] = [
    { key: "id", title: "ID", width: 120 },
    { key: "guest", title: t("accessRequests.guestName") },
    { key: "start", title: t("accessRequests.startingAt") },
    { key: "end", title: t("accessRequests.endingAt") },
    { key: "otp", title: t("accessRequests.otp") },
    {
      key: "requestedBy",
      title: t("accessRequests.requestedBy"),
    },
    { key: "condo", title: t("accessRequests.condoNumber") },
    { key: "req", title: t("accessRequests.requestDateAndTime") },
    {
      key: "status",
      title: t("accessRequests.requestStatus"),
      align: "center",
      render: (value) => (
        <Pills
          label={value}
          variant={
            value === "Active"
              ? "success"
              : value === "Revoked" || value === "Used"
              ? "danger"
              : "ghost"
          }
          size="sm"
        />
      ),
    },
    //  {
    //    key: "actions",
    //    title: "",
    //    render: (_, row) => <MyDropdown row={row} />,
    //  },
  ];
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("pastGuestAccess.title")}
            pageTitles={[
              { label: t("accessRequests.dashboard"), path: "/" },
              { label: t("pastGuestAccess.title"), path: "/past-guest-access" },
            ]}
          />
          <Row className="align-items-center justify-content-between">
            <Col xs={12} md={8} lg={8} className="p-0">
              <Heading
                title={t("pastGuestAccess.title")}
                description={t("pastGuestAccess.reviewExpiredDescription")}
                button={false}
              />
            </Col>
            <Col xs="auto" className="d-flex justify-content-end">
              <Filter
                placeholder={t("pastGuestAccess.searchGuests")}
                value={search}
                onSearch={handleSearch}
                onClear={() => {
                  setSearch("");
                  setLastSearch("");
                  setPage(1);
                }}
              />
            </Col>
          </Row>
          {getRequestsMutation.isError ? (
            <div className="text-center my-5">
              <div className="text-danger mb-3">
                <i
                  className="ri-error-warning-line"
                  style={{ fontSize: "3rem" }}
                />
              </div>
              <h5 className="text-danger">{t("accessRequests.failedToLoad")}</h5>
              <p className="text-muted">
                {(getRequestsMutation.error as any)?.message ||
                  t("accessRequests.pleaseTryAgain")}
              </p>
              <Button
                className="btn btn-secondary"
                onClick={() =>
                  getRequestsMutation.mutate({ page, limit, search })
                }
              >
                {t("accessRequests.retry")}
              </Button>
            </div>
          ) : (
            <Table
              columns={columns}
              data={accessData}
              pagination={{
                pageIndex: page - 1,
                pageSize: limit,
                total: paginationData.total,
              }}
              onPageChange={handlePageChange}
              loading={getRequestsMutation.isPending}
              selectable
              onSelectionChange={(sel) => console.log("selected", sel)}
            />
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};
export default AccessHistory;
